
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { isAccountSetupLink, completeAccountSetup, verifyEmail } from '@/services/auth/emailVerification';
import { getInvitationByEmail } from '@/services/auth/userManagement';

export const useAccountSetup = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(true); // Start as true to show verification screen first
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  useEffect(() => {
    // Check if the current URL is a valid sign-in link or verification link
    const valid = isAccountSetupLink();
    setIsLinkValid(valid);
    
    if (!valid) {
      toast({
        title: "Invalid or expired link",
        description: "This account setup link is invalid or has expired. Please request a new invitation.",
        variant: "destructive"
      });
      setIsVerifying(false);
      return;
    }
    
    // Handle verification email if present
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const emailParam = params.get('email');
    
    if (mode === 'verifyEmail') {
      verifyEmail().then(result => {
        setIsVerifying(false);
        if (result.success) {
          toast({
            title: "Email verified",
            description: "Your email has been verified. Please complete your account setup."
          });
          // After successful verification, ensure we load the pending invite data
          if (emailParam) {
            loadInvitationDataByEmail(emailParam);
          } else {
            loadPendingInviteData();
          }
        } else {
          toast({
            title: "Verification failed",
            description: result.error || "There was an error verifying your email.",
            variant: "destructive"
          });
          // Still try to load invite data even if verification fails
          if (emailParam) {
            loadInvitationDataByEmail(emailParam);
          } else {
            loadPendingInviteData();
          }
        }
      });
    } else {
      // If not verifying, load the invite data immediately
      if (emailParam) {
        loadInvitationDataByEmail(emailParam);
      } else {
        loadPendingInviteData();
      }
      setIsVerifying(false);
    }
  }, [toast]);
  
  // Function to load invitation data by email (from Firestore)
  const loadInvitationDataByEmail = async (emailAddress: string) => {
    console.log("Attempting to load invitation data for email:", emailAddress);
    const inviteData = await getInvitationByEmail(emailAddress);
    
    if (inviteData) {
      console.log("Successfully loaded invitation data:", inviteData);
      setEmail(inviteData.email || '');
      setFirstName(inviteData.firstName || '');
      setLastName(inviteData.lastName || '');
    } else {
      console.log("No invitation data found for email:", emailAddress);
      // If no invite data found, at least set the email from the URL
      setEmail(emailAddress);
    }
  };
  
  // Separate function to load pending invite data from localStorage
  const loadPendingInviteData = () => {
    try {
      const pendingInviteData = localStorage.getItem('pendingInvite');
      console.log("Loading pending invite data from localStorage:", pendingInviteData);
      
      if (pendingInviteData) {
        const inviteData = JSON.parse(pendingInviteData);
        console.log("Parsed invite data:", inviteData);
        
        // Auto-fill form fields with invite data
        if (inviteData.email) {
          console.log("Setting email to:", inviteData.email);
          setEmail(inviteData.email);
        }
        
        if (inviteData.firstName) {
          console.log("Setting firstName to:", inviteData.firstName);
          setFirstName(inviteData.firstName);
        }
        
        if (inviteData.lastName) {
          console.log("Setting lastName to:", inviteData.lastName);
          setLastName(inviteData.lastName);
        }
      } else {
        console.log("No pending invite data found in localStorage");
        // If no invite data, try to extract email from URL parameters
        const params = new URLSearchParams(window.location.search);
        const emailFromParams = params.get('email');
        
        if (emailFromParams) {
          console.log("Found email from URL params:", emailFromParams);
          setEmail(emailFromParams);
          
          // Try to load invitation data from Firestore
          loadInvitationDataByEmail(emailFromParams);
        } else {
          // Prompt user to enter email if not found
          toast({
            title: "Email Required",
            description: "Please enter the email address that received the invitation.",
          });
        }
      }
    } catch (e) {
      console.error('Error loading invitation data:', e);
      toast({
        title: "Error loading invitation data",
        description: "There was an error loading your invitation details.",
        variant: "destructive"
      });
    }
  };
  
  const validateForm = () => {
    if (!email) {
      setValidationError('Email is required');
      return false;
    }
    
    if (!firstName || !lastName) {
      setValidationError('First and last name are required');
      return false;
    }
    
    if (password.length < 6) {
      setValidationError('Password must be at least 6 characters long');
      return false;
    }
    
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return false;
    }
    
    setValidationError('');
    return true;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      const displayName = `${firstName} ${lastName}`;
      const result = await completeAccountSetup(email, password, displayName);
      
      if (result.success) {
        // Clear the pending invite data after successful account setup
        localStorage.removeItem('pendingInvite');
        
        toast({
          title: "Account setup complete",
          description: "Your account has been set up successfully."
        });
        
        // Redirect to dashboard or home page
        navigate('/');
      } else {
        toast({
          title: "Account setup failed",
          description: result.error || "There was an error setting up your account.",
          variant: "destructive"
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "There was an error setting up your account.";
      toast({
        title: "Account setup failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    email,
    setEmail,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    isLoading,
    isLinkValid,
    validationError,
    isVerifying,
    handleSubmit
  };
};
